<template>
  <div class="checkout-container">
    <stepper-progress :current-step="4"></stepper-progress>
    
    <div class="success-wrapper">
      <div class="main-content">
        <div class="donation-card">
          <div class="donation-title">
            <i class="mdi mdi-check-circle" style="color: #4a6b22; font-size: 24px;"></i>
            Thank you for your booking
          </div>

          <div class="card-content">
            <p>
              Thank you,
              <span style="text-transform: capitalize">{{ userFirstName }}</span>
            </p>
            <p><strong>Your booking has been confirmed.</strong></p>

            <div class="payment-instructions">
              <p>
                Booking reference:
                <strong>{{ singleBooking.booking_ref_code }}</strong>
              </p>
              <p>
                Total Number of Guests:
                <b>{{ singleBooking.guest_total }}</b>
              </p>
            </div>

            <!-- Dates Selected -->
            <div class="mt-4">
              <h3 class="methods-title">Dates Selected:</h3>
              <ul>
                <li>
                  Booking Checkin Date:
                  <b>{{ formatDate(singleBooking.check_in_date) }}</b>
                </li>
                <li>
                  Booking Check Out Date:
                  <b>{{ formatDate(singleBooking.check_out_date) }}</b>
                </li>
              </ul>
            </div>

            <!-- Conservation Entry Fees - Detailed Format -->
            <div class="mt-4" v-if="singleBooking.guests && singleBooking.guests.length > 0">
              <h3 class="methods-title">Conservation Entry Fees:</h3>
              <ul>
                <li v-for="(guest, key) in singleBooking.guests"
                    v-if="guest.payment_guests > 0"
                    :key="key">
                  <strong>{{ guest.payment_person }}</strong><br>
                  <span style="margin-left: 20px;">
                    Quantity: {{ guest.payment_guests }} guest(s)<br>
                    Rate: {{ singleBooking.currency }} {{ formatCurrency(guest.payment_person_amount) }} per guest<br>
                    Duration: {{ guest.no_of_nights }} day(s)<br>
                    <strong>Total: {{ singleBooking.currency }} {{ formatCurrency(guest.payment_person_cost_after_discount) }}</strong>
                  </span>
                </li>
              </ul>
            </div>

            <!-- Accommodation Selected - Using correct field names -->
            <div class="mt-4" v-if="hasFacilityBookings">
              <h3 class="methods-title">Accommodation Selected:</h3>
              <ul>
                <li v-for="(facility, key) in singleBooking.facility_bookings" :key="key">
                  <strong>{{ facility.facility_name }}</strong><br>
                  <span style="margin-left: 20px;">
                    Guests: {{ facility.facility_booking_guests || 1 }} guest(s)<br>
                    Dates: {{ formatDate(facility.facility_booking_check_in_date) }} - {{ formatDate(facility.facility_booking_check_out_date) }}<br>
                    Duration: {{ facility.facility_no_of_nights || getDays(facility.facility_booking_check_in_date, facility.facility_booking_check_out_date) }} night(s)<br>
                    <strong>Total: {{ facility.facility_booking_currency || singleBooking.currency }} {{ formatCurrency(facility.facility_booking_total_cost || 0) }}</strong>
                  </span>
                </li>
              </ul>
            </div>
            
            <!-- Conservation Activities Selected - Detailed Format -->
            <div class="mt-4" v-if="hasActivityBookings">
              <h3 class="methods-title">Conservation Activities Selected:</h3>
              <ul>
                <li v-for="(activity, key) in singleBooking.inventory_bookings"
                    :key="key">
                  <strong>{{ activity.inventory_name }}</strong><br>
                  <span style="margin-left: 20px;">
                    Date: {{ formatDate(activity.inventory_booking_date) }}<br>
                    Adults: {{ activity.inventory_booking_adults }}<br>
                    Children: {{ activity.inventory_booking_children }}<br>
                    <strong>Total: {{ singleBooking.currency }} {{ formatCurrency(activity.inventory_booking_total_cost) }}</strong>
                  </span>
                </li>
              </ul>
            </div>

            <!-- Vehicle Fee Section - Detailed Format -->
            <div class="mt-4" v-if="hasVehicleFee">
              <h3 class="methods-title">Vehicle Fee:</h3>
              <ul>
                <li v-for="(vehicle, key) in singleBooking.vehicles" 
                    v-if="vehicle.vehicles > 0"
                    :key="key">
                  <strong>{{ vehicle.vehicle_charge_category }}</strong><br>
                  <span style="margin-left: 20px;">
                    Quantity: {{ vehicle.vehicles }} vehicle(s)<br>
                    Rate: {{ singleBooking.currency }} {{ formatCurrency(vehicle.vehicle_charge_category_cost) }} per vehicle per day<br>
                    Duration: {{ vehicle.vehicle_no_of_nights }} day(s)<br>
                    <strong>Total: {{ singleBooking.currency }} {{ formatCurrency(vehicle.vehicle_cost_after_discount) }}</strong>
                  </span>
                </li>
              </ul>
            </div>

            <!-- Amount Paid -->
            <div class="mt-4">
              <h3 class="methods-title">Amount Paid:</h3>
              <p>
                <b style="color: #4a6b22; font-size: 20px;">
                  {{ singleBooking.currency }}
                  {{
                    formatCurrency(singleBooking.total_cost)
                  }}
                </b>
              </p>
              <!-- Payment Status - Conditional Display -->
              <div class="paid-status" v-if="singleBooking.booking_payment_status === 'Paid'">
                <i class="mdi mdi-check-circle" style="color: #4a6b22;"></i>
                <span style="color: #4a6b22; font-weight: bold;">FULLY PAID</span>
              </div>
              <div class="paid-status-pending" v-else-if="singleBooking.booking_payment_status === 'Not Paid' || !singleBooking.booking_payment_status">
                <i class="mdi mdi-clock-outline" style="color: #ffc107;"></i>
                <span style="color: #ffc107; font-weight: bold;">PENDING PAYMENT</span>
              </div>
              <div class="paid-status-partial" v-else-if="singleBooking.booking_payment_status === 'Incomplete Payment'">
                <i class="mdi mdi-alert-circle-outline" style="color: #ff9800;"></i>
                <span style="color: #ff9800; font-weight: bold;">PARTIAL PAYMENT</span>
              </div>
              <div class="paid-status-abandoned" v-else-if="singleBooking.booking_payment_status === 'Abandoned'">
                <i class="mdi mdi-close-circle" style="color: #dc3545;"></i>
                <span style="color: #dc3545; font-weight: bold;">ABANDONED</span>
              </div>
              <div class="paid-status-cancelled" v-else-if="singleBooking.booking_payment_status === 'Cancelled'">
                <i class="mdi mdi-cancel" style="color: #6c757d;"></i>
                <span style="color: #6c757d; font-weight: bold;">CANCELLED</span>
              </div>
            </div>

            <!-- Donations Section -->
            <div class="mt-4" v-if="donationList.length > 0">
              <h3 class="methods-title">Your Donations:</h3>
              <ul>
                <li v-for="(donation, key) in donationList" :key="key">
                  {{ donation.currency_name }} {{ formatCurrency(donation.amount) }} - {{ donation.cause_name || 'Conservation Donation' }}
                </li>
              </ul>
            </div>

            <!-- Wallet Info for Logged-in Users -->
            <div class="wallet-info mt-4" v-if="isLoggedIn && walletBalanceKes > 0">
              <div class="alert-success">
                <i class="mdi mdi-wallet"></i>
                <span>
                  Payment was processed from your wallet. 
                  Current balance: <strong>{{ formatCurrency(walletBalanceKes) }} KES</strong>
                </span>
              </div>
            </div>

            <div class="email-confirmation">
              <i class="mdi mdi-email"></i>
              <p>
                An email confirmation has been sent to
                <b>{{ getBookingEmail }}</b>
              </p>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons mt-4">
              <button 
                class="btn-primary"
                @click="viewMyBookings">
                <i class="mdi mdi-book-open-page-variant"></i>
                View My Bookings
              </button>
              
              <button 
                class="btn-wallet"
                @click="viewMyWallet">
                <i class="mdi mdi-wallet"></i>
                View My Wallet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StepperProgress from './StepperProgress.vue';

export default {
  components: {
    StepperProgress
  },
  name: "GuestSuccess",
  data() {
    return {
      walletBalanceKes: 0,
      walletBalanceUsd: 0,
      refreshInterval: null,
      donationList: []
    };
  },
  
  computed: {
    singleBooking() {
      var booking = null;
      
      if (this.$store.state.BookingStore) {
        booking = this.$store.state.BookingStore.single_booking || 
                 this.$store.state.BookingStore.booking ||
                 this.$store.state.BookingStore.current_booking;
      }
      
      if (!booking && this.$store.state.booking) {
        booking = this.$store.state.booking.single_booking ||
                 this.$store.state.booking.booking;
      }
      
      return booking || {};
    },
    
    getBookingEmail() {
      if (this.userEmail) {
        return this.userEmail;
      }
      if (this.singleBooking && this.singleBooking.booking_details && this.singleBooking.booking_details.email_address) {
        return this.singleBooking.booking_details.email_address;
      }
      return '';
    },
    
    isLoggedIn() {
      var userPublicId = localStorage.getItem('user_public_id');
      var userEmail = localStorage.getItem('user_email');
      var token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      
      return !!(userPublicId && userPublicId !== 'null' && userPublicId !== 'undefined' && userEmail && token);
    },
    
    userFirstName() {
      var firstName = localStorage.getItem('user_first_name') || 
             localStorage.getItem('first_name');
      
      if (!firstName && this.singleBooking && this.singleBooking.booking_details && this.singleBooking.booking_details.first_name) {
        firstName = this.singleBooking.booking_details.first_name;
      }
      
      return firstName || 'Guest';
    },
    
    userEmail() {
      var email = localStorage.getItem('user_email') || 
             localStorage.getItem('email');
      
      if (!email && this.singleBooking && this.singleBooking.booking_details && this.singleBooking.booking_details.email_address) {
        email = this.singleBooking.booking_details.email_address;
      }
      
      return email;
    },
    
    userPublicId() {
      return localStorage.getItem('user_public_id');
    },

    hasFacilityBookings() {
      return this.singleBooking && 
             this.singleBooking.facility_bookings && 
             this.singleBooking.facility_bookings.length > 0;
    },
    
    hasVehicleFee() {
      return this.singleBooking && 
             this.singleBooking.vehicles && 
             this.singleBooking.vehicles.some(v => v.vehicles > 0);
    },
    
    hasActivityBookings() {
      return this.singleBooking && 
             this.singleBooking.inventory_bookings && 
             this.singleBooking.inventory_bookings.length > 0;
    }
  },
  
  methods: {
    formatDate(date) {
      if (!date) return '';
      if (this.$moment) {
        return this.$moment(date).format('MMM DD, YYYY');
      }
      return new Date(date).toLocaleDateString();
    },
    
    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0';
      return parseFloat(amount).toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
    },
    
    loadWalletBalance() {
      var self = this;
      if (!this.isLoggedIn || !this.userPublicId) return;
      
      this.$store.dispatch('get_wallet_balance', this.userPublicId)
        .then(function(res) {
          if (res && res.status === 'success') {
            self.walletBalanceKes = parseFloat(res.data.wallet_balance_kes) || 0;
            self.walletBalanceUsd = parseFloat(res.data.wallet_balance_usd) || 0;
          }
        })
        .catch(function(err) {
          console.error('Error loading wallet balance:', err);
        });
    },

    loadDonations() {
      if (this.singleBooking && this.singleBooking.donations) {
        this.donationList = this.singleBooking.donations;
      }
    },
    
    viewMyBookings() {
      this.$router.push({ 
        name: 'MyBookings' 
      });
    },

    viewMyWallet() {
      this.$router.push({ 
        name: 'MyWallet'
      });
    },

    refreshBooking() {
      var bookingId = this.$route.params.booking_id;
      if (bookingId && this.$store.dispatch) {
        this.$store.dispatch("get_booking", bookingId);
      }
    }
  },
  
  created() {
    var bookingId = this.$route.params.booking_id;

    if (bookingId && this.$store.dispatch) {
      this.$store.dispatch("get_booking", bookingId);
    }
    
    this.loadWalletBalance();
    this.loadDonations();
  },
  
  mounted() {
    var self = this;

    this.refreshInterval = setInterval(function() {
      if (self.singleBooking && self.singleBooking.booking_payment_status !== 'Paid') {
        self.refreshBooking();
      }
    }, 30000);
  },
  
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
};
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('../PizzaDude.dk-Earthwerk.otf');
    font-weight: normal;
    font-style: normal;
  }

  /* ─── CHECKOUT CONTAINER ─── */
  .checkout-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    position: relative;
  }

  /* ─── SUCCESS WRAPPER ─── */
  .success-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px 0;
    position: relative;
  }

  /* ─── MAIN CONTENT ─── */
  .main-content {
    background-color: white;
    border-radius: 10px;
    padding: 25px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  }

  /* ─── DONATION CARD ─── */
  .donation-card {
    background-color: #f5f9f0;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 30px;
    border: 1px solid #e0e8d0;
  }

  .donation-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1.5rem;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    color: #4a6b22;
  }

  /* ─── METHODS TITLE ─── */
  .methods-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1.2rem;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    color: #4a6b22;
    border-left: 4px solid #74a533;
    padding-left: 12px;
  }

  .payment-instructions {
    margin-top: 15px;
    padding: 10px;
    background-color: #f5f5f5;
    border-radius: 5px;
    font-size: 0.9rem;
    color: #666;
  }

  .email-confirmation {
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #fff8e1;
    border-radius: 5px;
    font-size: 0.9rem;
    margin-top: 20px;
  }

  .email-confirmation i {
    margin-right: 10px;
    color: #ffc107;
  }

  /* ─── ALERT SUCCESS ─── */
  .alert-success {
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #d4edda;
    border-radius: 5px;
    font-size: 0.9rem;
    margin-top: 10px;
    color: #155724;
  }

  .alert-success i {
    margin-right: 10px;
    color: #28a745;
  }

  /* ─── PAID STATUS STYLES ─── */
  .paid-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #d4edda;
    padding: 5px 15px;
    border-radius: 20px;
    margin-top: 5px;
  }

  .paid-status-pending {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #fff3cd;
    padding: 5px 15px;
    border-radius: 20px;
    margin-top: 5px;
  }

  .paid-status-partial {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #ffe0b2;
    padding: 5px 15px;
    border-radius: 20px;
    margin-top: 5px;
  }

  .paid-status-abandoned {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #f8d7da;
    padding: 5px 15px;
    border-radius: 20px;
    margin-top: 5px;
  }

  .paid-status-cancelled {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #e9ecef;
    padding: 5px 15px;
    border-radius: 20px;
    margin-top: 5px;
  }

  /* ─── ACTION BUTTONS ─── */
  .action-buttons {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-top: 20px;
    flex-wrap: wrap;
  }

  .btn-primary {
    background-color: #74a533;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .btn-primary:hover {
    background-color: #5a8230;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }

  .btn-wallet {
    background-color: #6f42c1;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .btn-wallet:hover {
    background-color: #5a32a3;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }

  /* ─── EARTH COLOR ─── */
  .earth-color {
    color: #4a6b22;
  }

  /* ─── LISTS ─── */
  ul {
    list-style: none;
    padding-left: 0;
  }

  li {
    padding: 12px 0;
    border-bottom: 1px solid #e0e8d0;
  }

  li:last-child {
    border-bottom: none;
  }

  /* ─── MARGIN CLASSES ─── */
  .mt-4 {
    margin-top: 20px;
  }

  /* ─── RESPONSIVE ─── */
  @media (max-width: 960px) {
    .checkout-container {
      padding: 15px;
    }

    .success-wrapper {
      padding: 15px 0;
    }

    .main-content {
      margin-left: 0 !important;
      width: 100%;
    }
  }

  @media (max-width: 600px) {
    .checkout-container {
      padding: 10px;
    }

    .success-wrapper {
      padding: 10px 0;
    }

    .main-content {
      padding: 15px;
    }

    .donation-title {
      font-size: 1.2rem;
    }

    .methods-title {
      font-size: 1.1rem;
    }

    .action-buttons {
      flex-direction: column;
    }

    .btn-primary, .btn-wallet {
      width: 100%;
      justify-content: center;
    }
  }

  /* ─── PRINT STYLES ─── */
  @media print {
    .action-buttons,
    .payment-progress,
    .wallet-info {
      display: none;
    }
    
    .donation-card {
      box-shadow: none;
      padding: 0;
    }
    
    .payment-header {
      background: none;
      box-shadow: none;
    }
  }
</style>